import React, { useEffect, useState } from 'react';
import {
  Table, Card, Button, Modal, Form, Input, InputNumber, Tag, Space, message, Drawer,
} from 'antd';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { PlusOutlined, TeamOutlined, WalletOutlined } from '@ant-design/icons';

/**
 * B2B Organizations — create companies, invite employees by phone,
 * top up the session credit pool, watch usage. All writes go through
 * Cloud Functions (admin-gated); reads are direct from Firestore.
 */
const Organizations = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const [topupForm] = Form.useForm();

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'organizations')), (snap) => {
      setOrgs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const createOrg = async (values) => {
    try {
      const callable = httpsCallable(functions, 'createOrganization');
      await callable({ name: values.name, employeeRatePerMin: Math.round((values.employeeRate || 1) * 100) });
      message.success('Organization created');
      setCreateOpen(false);
      form.resetFields();
    } catch (e) {
      message.error(e.message || 'Failed to create');
    }
  };

  const inviteEmployee = async (values) => {
    try {
      const callable = httpsCallable(functions, 'addOrganizationInvite');
      await callable({ phone: values.phone, organizationId: selectedOrg.id });
      message.success(`Invite sent to ${values.phone}`);
      setInviteOpen(false);
      inviteForm.resetFields();
    } catch (e) {
      message.error(e.message || 'Failed to invite');
    }
  };

  const topUp = async (values) => {
    try {
      const callable = httpsCallable(functions, 'topUpOrganizationCredits');
      await callable({ organizationId: selectedOrg.id, amountPaise: Math.round(values.amount * 100), note: values.note || '' });
      message.success(`₹${values.amount} added to the pool`);
      setTopupOpen(false);
      topupForm.resetFields();
    } catch (e) {
      message.error(e.message || 'Failed to top up');
    }
  };

  const openMembers = async (org) => {
    setSelectedOrg(org);
    setMembersOpen(true);
    try {
      const callable = httpsCallable(functions, 'listOrganizationMembers');
      const res = await callable({ organizationId: org.id });
      setMembers(res.data.members || []);
    } catch (e) {
      message.error(e.message || 'Failed to load members');
    }
  };

  const columns = [
    { title: 'Organization', dataIndex: 'name', key: 'name' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => <Tag color={s === 'active' ? 'green' : 'red'}>{s.toUpperCase()}</Tag>,
    },
    {
      title: 'Credit Pool', dataIndex: 'creditsBalance', key: 'credits',
      render: (v) => `₹${((v || 0) / 100).toLocaleString('en-IN')}`,
    },
    {
      title: 'Employee Rate', dataIndex: 'employeeRatePerMin', key: 'rate',
      render: (v) => `₹${((v || 100) / 100).toFixed(2)}/min`,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button size="small" icon={<TeamOutlined />} onClick={() => openMembers(record)}>Members</Button>
          <Button size="small" icon={<WalletOutlined />}
            onClick={() => { setSelectedOrg(record); setTopupOpen(true); }}>Top up</Button>
          <Button size="small" type="primary" ghost
            onClick={() => { setSelectedOrg(record); setInviteOpen(true); }}>+ Invite employee</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600 }}>Organizations</h1>
      <p style={{ color: '#888', maxWidth: 520 }}>
        B2B companies, their session credit pools, and employee invites. Employees join automatically
        when they sign in with an invited phone number.
      </p>

      <Card
        title="Companies"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>New organization</Button>}
        style={{ marginTop: 16 }}
      >
        <Table columns={columns} dataSource={orgs} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />
      </Card>

      {/* Create org */}
      <Modal title="New organization" open={createOpen} onCancel={() => setCreateOpen(false)} footer={null}>
        <Form layout="vertical" form={form} onFinish={createOrg}>
          <Form.Item name="name" label="Company name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. Acme India Pvt Ltd" />
          </Form.Item>
          <Form.Item name="employeeRate" label="Employee pays (₹ per minute)" initialValue={1}
            rules={[{ required: true, message: 'Rate required' }]}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Create</Button>
        </Form>
      </Modal>

      {/* Invite employee */}
      <Modal title={`Invite employee — ${selectedOrg?.name || ''}`} open={inviteOpen} onCancel={() => setInviteOpen(false)} footer={null}>
        <Form layout="vertical" form={inviteForm} onFinish={inviteEmployee}>
          <Form.Item name="phone" label="Employee phone (with +91)" rules={[{ required: true, message: 'Phone required' }]}>
            <Input placeholder="+91 98765 43210" />
          </Form.Item>
          <p style={{ color: '#888', fontSize: 12, marginBottom: 16, marginTop: -8 }}>
            They'll be covered automatically the first time they sign in with this number.
          </p>
          <Button type="primary" htmlType="submit" block>Send invite</Button>
        </Form>
      </Modal>

      {/* Top up pool */}
      <Modal title={`Top up pool — ${selectedOrg?.name || ''}`} open={topupOpen} onCancel={() => setTopupOpen(false)} footer={null}>
        <Form layout="vertical" form={topupForm} onFinish={topUp}>
          <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true, message: 'Amount required' }]}>
            <InputNumber min={100} step={500} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)">
            <Input.TextArea rows={2} placeholder="e.g. May top-up under annual plan" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>Add credits</Button>
        </Form>
      </Modal>

      {/* Members drawer */}
      <Drawer title={`Members — ${selectedOrg?.name || ''}`} placement="right" width={520} onClose={() => setMembersOpen(false)} open={membersOpen}>
        <Table
          dataSource={members}
          rowKey="uid"
          size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'User', dataIndex: 'uid', key: 'uid', render: (t) => <span>{t.substring(0, 12)}…</span> },
            { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'active' ? 'green' : 'red'}>{s}</Tag> },
            {
              title: '', key: 'x',
              render: (_, r) => (
                <Button size="small" danger
                  onClick={async () => {
                    try {
                      const callable = httpsCallable(functions, 'removeOrganizationMember');
                      await callable({ uid: r.uid });
                      message.success('Member removed');
                      openMembers(selectedOrg);
                    } catch (e) { message.error(e.message || 'Failed'); }
                  }}>
                  Remove
                </Button>
              ),
            },
          ]}
        />
      </Drawer>
    </div>
  );
};

export default Organizations;